const state = {
  jobs: {},
  pollingTimeouts: {},
};

const getters = {
  jobs: (state) => state.jobs,
  getJob: (state) => (jobId) => state.jobs[jobId],
};

const mutations = {
  setJob(state, { jobId, data }) {
    state.jobs[jobId] = data;
  },
  setPollingTimeout(state, { jobId, timeoutId }) {
    state.pollingTimeouts[jobId] = timeoutId;
  },
  clearPollingTimeout(state, jobId) {
    if (state.pollingTimeouts[jobId]) {
      clearTimeout(state.pollingTimeouts[jobId]);
      delete state.pollingTimeouts[jobId];
    }
  },
};

const actions = {
  createJob({ commit, dispatch, rootGetters }, { jobName, jobParams }) {
    // Extract existingConversationId before sending to API (it's not an API param)
    const { existingConversationId, ...apiJobParams } = jobParams;

    const params = {
      job: {
        job_name: jobName,
        job_params: apiJobParams,
      },
    };
    return rootGetters['session/ajax'].post('/jobs', params).then(response => {
      const jobId = response.data.jobId;
      const startTime = Date.now();
      commit('setJob', {
        jobId,
        data: {
          status: 'queued',
          result: null,
          pollCount: 0,
          prevDelay: 1000,
          currentDelay: 2000,
          startTime,
          polling: true,
          existingConversationId: existingConversationId || null,
        },
      });
      // Set initial interim response when job starts
      dispatch('mira/setInterimResponse', true, { root: true });
      // Start polling with initial 2 second delay (Fibonacci: 2s, 3s, 5s, 8s, ...)
      dispatch('scheduleJobPoll', { jobId, prevDelay: 1000, currentDelay: 2000 });
      return response;
    });
  },
  createOpenAiChatJob({ dispatch }, jobParams) {
    return dispatch('createJob', {
      jobName: 'OpenAiChatJob',
      jobParams,
    });
  },
  createConversationChatJob({ dispatch }, jobParams) {
    return dispatch('createJob', {
      jobName: 'ConversationChatJob',
      jobParams,
    });
  },
  getJobStatus({ commit, rootGetters }, jobId) {
    return rootGetters['session/ajax'].get(`/jobs/status/${jobId}`).then(response => {
      commit('setJob', { jobId, data: response.data });
      return response;
    });
  },
  pollJobStatus({ state, commit, dispatch, rootGetters }, jobId) {
    const job = state.jobs[jobId];
    if (!job || !job.polling) {
      return Promise.resolve();
    }

    // Check if max poll count reached
    const MAX_POLL_COUNT = 15;
    if (job.pollCount >= MAX_POLL_COUNT) {
      commit('clearPollingTimeout', jobId);
      commit('setJob', {
        jobId,
        data: { ...job, polling: false, timedOut: true },
      });
      // Mark the temporary conversation as timed out
      dispatch('conversations/markConversationAsTimedOut', jobId, { root: true }).catch(error => {
        console.error('Error marking conversation as timed out:', error);
      });
      return Promise.resolve();
    }

    // Check if 2 minutes have passed
    const elapsed = Date.now() - job.startTime;
    const MAX_POLLING_TIME = 2 * 60 * 1000; // 2 minutes
    if (elapsed >= MAX_POLLING_TIME) {
      commit('clearPollingTimeout', jobId);
      commit('setJob', {
        jobId,
        data: { ...job, polling: false, timedOut: true },
      });
      // Mark the temporary conversation as timed out
      dispatch('conversations/markConversationAsTimedOut', jobId, { root: true }).catch(error => {
        console.error('Error marking conversation as timed out:', error);
      });
      return Promise.resolve();
    }

    return rootGetters['session/ajax'].get(`/jobs/status/${jobId}`).then(response => {
      const updatedJob = {
        ...job,
        status: response.data.status,
        result: response.data.result,
        pollCount: job.pollCount + 1,
      };
      // Check if job is complete or failed
      if (response.data.status === 'completed' || response.data.status === 'failed') {
        updatedJob.polling = false;
        updatedJob.fullResponse = response.data;
        commit('clearPollingTimeout', jobId);

        // Clear interim response when completed
        if (response.data.status === 'completed') {
          dispatch('mira/setInterimResponse', false, { root: true });
        }

        // If this is a ConversationChatJob and it completed successfully
        if (response.data.status === 'completed' && response.data.result?.conversationId) {
          // Check if this was for an existing conversation or a new one
          if (job.existingConversationId) {
            // Add assistant message to existing conversation
            dispatch('conversations/addAssistantMessageFromJob', {
              conversationId: job.existingConversationId,
              jobResult: response.data,
            }, { root: true }).catch(error => {
              console.error('Error adding assistant message:', error);
            });
            // Stop polling
            dispatch('mira/setPolling', false, { root: true });
          } else {
            // Resolve temporary conversation (new conversation flow)
            dispatch('conversations/resolveTemporaryConversation', {
              jobId,
              jobResult: response.data,
            }, { root: true }).catch(error => {
              console.error('Error resolving temporary conversation:', error);
            });
          }
        } else if (response.data.status === 'failed') {
          // Mark the temporary conversation as failed
          dispatch('conversations/markConversationAsFailed', {
            conversationId: jobId,
            error: response.data.error || 'Job failed',
          }, { root: true }).catch(error => {
            console.error('Error marking conversation as failed:', error);
          });
          // Stop polling for existing conversations too
          if (job.existingConversationId) {
            dispatch('mira/setPolling', false, { root: true });
          }
        }
      } else if (response.data.status !== 'failed') {
        // Set random interim response for non-failed, non-completed statuses (queued, processing, etc.)
        dispatch('mira/setInterimResponse', true, { root: true });
      }

      commit('setJob', { jobId, data: updatedJob });

      // Schedule next poll if still running
      if (updatedJob.polling) {
        // Calculate next Fibonacci delay, capped at 30 seconds
        const MAX_DELAY = 30000;
        const nextDelay = Math.min(job.prevDelay + job.currentDelay, MAX_DELAY);
        dispatch('scheduleJobPoll', {
          jobId,
          prevDelay: job.currentDelay,
          currentDelay: nextDelay,
        });
      }

      return response;
    }).catch(error => {
      console.error('Error polling job status:', error);
      // Stop polling and mark as failed
      commit('clearPollingTimeout', jobId);
      commit('setJob', {
        jobId,
        data: { ...job, polling: false, failed: true },
      });
      // Mark the conversation as failed
      dispatch('conversations/markConversationAsFailed', {
        conversationId: job.existingConversationId || jobId,
        error: 'Network error while checking job status',
      }, { root: true }).catch(err => {
        console.error('Error marking conversation as failed:', err);
      });
      // Clear interim response
      dispatch('mira/setInterimResponse', false, { root: true });
      // Stop polling for existing conversations
      if (job.existingConversationId) {
        dispatch('mira/setPolling', false, { root: true });
      }
    });
  },
  scheduleJobPoll({ state, commit, dispatch }, { jobId, prevDelay, currentDelay }) {
    const job = state.jobs[jobId];
    if (!job || !job.polling) {
      return;
    }

    // Update the delay values in the job
    commit('setJob', {
      jobId,
      data: { ...job, prevDelay, currentDelay },
    });

    const timeoutId = setTimeout(() => {
      dispatch('pollJobStatus', jobId);
    }, currentDelay);

    commit('setPollingTimeout', { jobId, timeoutId });
  },
  stopPolling({ state, commit }, jobId) {
    const job = state.jobs[jobId];
    if (job) {
      commit('clearPollingTimeout', jobId);
      commit('setJob', {
        jobId,
        data: { ...job, polling: false },
      });
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
